import { FC, ReactNode } from 'react';
import { isFilled } from '@prismicio/client';
import type { ImageField } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';

interface HeroSectionProps {
  sliceType: string;
  variation: string;
  backgroundImage?: ImageField;
  children: ReactNode;
}

/**
 * Shared outer `<section>` wrapper used by every `LeadCaptureHero` variant:
 * the horizontal gradient background, overflow guard, and the hero padding
 * (which clears the sticky SiteNavbar's negative margin on desktop). Each
 * variant is responsible for its own inner grid / column layout.
 */
const HeroSection: FC<HeroSectionProps> = ({
  sliceType,
  variation,
  backgroundImage,
  children,
}) => {
  const hasBackground = isFilled.image(backgroundImage);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={variation}
      /* First-on-page hero: top padding clears the sticky navbar (~86px
         overlap from SiteNavbar's negative margin) plus the 62px Figma gap
         below it on desktop; scaled down on mobile where the floating navbar
         is shorter. */
      className="relative w-full overflow-hidden bg-[linear-gradient(to_right,#0D6443_0%,#0C563B_12%,#0C4633_28%,#0C3A2E_60%,#0B312C_80%,#0B3531_100%)] px-5 pb-16 pt-40 md:px-16 md:pb-[80px] md:pt-[200px]"
    >
      {hasBackground && (
        <>
          <PrismicNextImage
            field={backgroundImage!}
            fill
            sizes="100vw"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            alt=""
          />
          {/* 40% black overlay restores enough contrast for white copy and the
              light form card on top of any backdrop. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-black/40"
          />
        </>
      )}
      {children}
    </section>
  );
};

export default HeroSection;
